import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useCustomerAuth } from "./CustomerAuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SavedLooksContext = createContext(null);

export function SavedLooksProvider({ children }) {
  const { customer } = useCustomerAuth();
  const [savedIds, setSavedIds] = useState(new Set());
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const fetchSavedIds = useCallback(async () => {
    if (!customer?.customer_id) { setSavedIds(new Set()); return; }
    try {
      const res = await axios.get(`${API}/saved-looks/ids`, {
        headers: { "x-customer-id": customer.customer_id },
      });
      setSavedIds(new Set(res.data.ids || []));
    } catch { setSavedIds(new Set()); }
  }, [customer?.customer_id]);

  useEffect(() => { fetchSavedIds(); }, [fetchSavedIds]);

  const toggleSave = useCallback(async (outfitId) => {
    if (!customer?.customer_id) {
      setShowLoginPrompt(true);
      return { needsLogin: true };
    }
    const alreadySaved = savedIds.has(outfitId);
    // Optimistic update
    setSavedIds(prev => {
      const n = new Set(prev);
      alreadySaved ? n.delete(outfitId) : n.add(outfitId);
      return n;
    });
    try {
      if (alreadySaved) {
        await axios.delete(`${API}/saved-looks/${outfitId}`, {
          headers: { "x-customer-id": customer.customer_id },
        });
      } else {
        await axios.post(`${API}/saved-looks`, { outfit_id: outfitId }, {
          headers: { "x-customer-id": customer.customer_id },
        });
      }
      // Fire-and-forget analytics
      axios.post(`${API}/analytics/track`, {
        event: alreadySaved ? "wishlist_remove" : "wishlist_add",
        outfit_id: outfitId,
      }, { headers: { "x-customer-id": customer.customer_id } }).catch(() => {});
      return { saved: !alreadySaved };
    } catch {
      // Revert on error
      setSavedIds(prev => {
        const n = new Set(prev);
        alreadySaved ? n.add(outfitId) : n.delete(outfitId);
        return n;
      });
      return { error: true };
    }
  }, [customer?.customer_id, savedIds]);

  const isSaved = useCallback((outfitId) => savedIds.has(outfitId), [savedIds]);
  const dismissLoginPrompt = useCallback(() => setShowLoginPrompt(false), []);

  return (
    <SavedLooksContext.Provider value={{ savedIds, isSaved, toggleSave, fetchSavedIds, count: savedIds.size, showLoginPrompt, dismissLoginPrompt }}>
      {children}
    </SavedLooksContext.Provider>
  );
}

export function useSavedLooks() {
  const ctx = useContext(SavedLooksContext);
  if (!ctx) throw new Error("useSavedLooks must be used within SavedLooksProvider");
  return ctx;
}
